import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { BookingBreadcrumbs } from '@features/booking/components/BookingBreadcrumbs';
import { BookingPageLayout } from '@features/booking/layouts';
import { useBookingFlow } from '@features/booking/flow';
import { useAnalytics, ANALYTICS_EVENTS } from '@core/analytics';
import { useBooking } from '@features/booking/state';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { useTimeSlots } from '@features/booking/hooks/useTimeSlots';

type FilterMode = 'any' | 'specific';

const SelectAreaFilterPage = () => {
  const { t } = useTranslation('booking');
  const { goToNext } = useBookingFlow();
  const { trackEvent } = useAnalytics();
  const booking = useBooking();
  const restaurant = useRestaurant();

  useEffect(() => {
    trackEvent({ name: ANALYTICS_EVENTS.BOOKING_SELECT_AREA_FILTER });
  }, [trackEvent]);

  // booking.timeSlots is overwritten with area-filtered slots after this step,
  // so we fetch the unfiltered set here to stay correct on back-navigation.
  const service = booking.service;
  const { getServiceSlots, isSettled } = useTimeSlots({
    restaurantId: restaurant.id,
    partySize: booking.diners,
    date: booking.selectedDate ?? '',
    serviceTypes: service ? [service] : [],
    enabled: !!booking.selectedDate && !!service,
    hasPreferredAccess: false,
  });

  const availableAreas = useMemo(() => {
    if (!service) return restaurant.publicAreas ?? [];
    const slots = getServiceSlots(service);
    const availableIds = new Set<number>();
    for (const slot of slots) {
      for (const area of slot.availableAreas ?? []) {
        if (area.available) {
          availableIds.add(area.id);
        }
      }
    }
    return (restaurant.publicAreas ?? []).filter((area) =>
      availableIds.has(area.id),
    );
  }, [getServiceSlots, service, restaurant.publicAreas]);

  const [filterMode, setFilterMode] = useState<FilterMode>(() => {
    if (booking.areaFilterIds && booking.areaFilterIds.length > 0) {
      return 'specific';
    }
    return 'any';
  });

  const [selected, setSelected] = useState<Set<number>>(() => {
    if (booking.areaFilterIds) {
      return new Set(booking.areaFilterIds);
    }
    return new Set<number>();
  });

  const areaFilterIds = booking.areaFilterIds;
  const setAreaFilterIds = booking.setAreaFilterIds;
  useEffect(() => {
    if (!isSettled) return;
    if (availableAreas.length < 2 && filterMode === 'specific') {
      setFilterMode('any');
      setSelected(new Set());
      setAreaFilterIds(null);
      return;
    }
    if (!areaFilterIds || areaFilterIds.length === 0) return;
    const availableIds = new Set(availableAreas.map((a) => a.id));
    const pruned = areaFilterIds.filter((id) => availableIds.has(id));
    if (pruned.length !== areaFilterIds.length) {
      setSelected(new Set(pruned));
      setAreaFilterIds(pruned.length > 0 ? pruned : null);
      if (pruned.length === 0) {
        setFilterMode('any');
      }
    }
  }, [isSettled, availableAreas, areaFilterIds, setAreaFilterIds, filterMode]);

  const toggleArea = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleModeChange = (mode: FilterMode) => {
    setFilterMode(mode);
    if (mode === 'any') {
      setSelected(new Set());
    }
  };

  const handleContinue = () => {
    // Keep IDs in a deterministic order so timeSlotsKeys.byService (which
    // sorts for the query key) and the fetch URL stay in sync across requests.
    const filter = filterMode === 'specific' && selected.size > 0
      ? Array.from(selected).sort((a, b) => a - b)
      : null;
    booking.setAreaFilterIds(filter);
    goToNext();
  };

  const canContinue = filterMode === 'any' || selected.size > 0;
  const canPickSpecific = availableAreas.length > 1;

  return (
    <BookingPageLayout
      title={t('AREA_FILTER_TITLE')}
      subtitle=""
      breadcrumbs={<BookingBreadcrumbs />}
    >
      <div className="w-full px-6 space-y-3" role="radiogroup" aria-label={t('AREA_TITLE')}>
        <button
          type="button"
          role="radio"
          aria-checked={filterMode === 'any'}
          onClick={() => handleModeChange('any')}
          className={`w-full py-4 px-5 rounded-xl border text-left transition-all duration-200 flex items-center justify-between ${
            filterMode === 'any'
              ? 'bg-brand text-brand-foreground border-brand shadow-sm'
              : 'bg-card text-foreground/70 border-border hover:border-brand-light'
          }`}
        >
          <span className="font-medium">{t('AREA_FILTER_ANY')}</span>
          {filterMode === 'any' && <Check className="w-5 h-5 shrink-0" aria-hidden="true" />}
        </button>

        {canPickSpecific && (
          <button
            type="button"
            role="radio"
            aria-checked={filterMode === 'specific'}
            onClick={() => handleModeChange('specific')}
            className={`w-full py-4 px-5 rounded-xl border text-left transition-all duration-200 flex items-center justify-between ${
              filterMode === 'specific'
                ? 'bg-brand text-brand-foreground border-brand shadow-sm'
                : 'bg-card text-foreground/70 border-border hover:border-brand-light'
            }`}
          >
            <span className="font-medium">{t('AREA_FILTER_SEARCH')}</span>
            {filterMode === 'specific' && <Check className="w-5 h-5 shrink-0" aria-hidden="true" />}
          </button>
        )}
      </div>

      {filterMode === 'specific' && (
        <div className="w-full px-6 mt-6 space-y-2">
          <p id="area-filter-select-label" className="text-sm text-foreground/50 mb-3">
            {t('AREA_FILTER_SELECT_AREAS')}
          </p>
          <div role="group" aria-labelledby="area-filter-select-label">
            {availableAreas.map((area) => (
              <button
                key={area.id}
                type="button"
                role="checkbox"
                aria-checked={selected.has(area.id)}
                onClick={() => toggleArea(area.id)}
                className={`w-full py-3 px-4 rounded-lg border text-left transition-all duration-200 flex items-center gap-3 ${
                  selected.has(area.id)
                    ? 'bg-brand/10 text-foreground border-brand'
                    : 'bg-card text-foreground/70 border-border hover:border-brand-light'
                }`}
              >
                <div
                  aria-hidden="true"
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                    selected.has(area.id)
                      ? 'bg-brand border-brand'
                      : 'border-foreground/30'
                  }`}
                >
                  {selected.has(area.id) && <Check className="w-3.5 h-3.5 text-brand-foreground" />}
                </div>
                <div className="flex flex-col items-start">
                  <span className="font-medium text-sm">{area.name}</span>
                  {area.description && (
                    <span className="text-xs text-foreground/50">{area.description}</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="w-full px-6 mt-12">
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md disabled:opacity-50"
          onClick={handleContinue}
          disabled={!canContinue}
        >
          {t('FILTER_SELECT_MENU_EXPERIENCE_APPLY')}
        </Button>
      </div>
    </BookingPageLayout>
  );
};

export default SelectAreaFilterPage;
